import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  OnInit,
  Input,
} from '@angular/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { RfqWrapperComponent } from '../rfq-wrapper/rfq-wrapper.component';
import { Router } from '@angular/router';
import { RequestForQuotationService } from '@/app/services/tendering-services/rfq/rfq.service';
import { NgToastService } from 'ng-angular-popup';
import { DatePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-rfq-key-dates',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    MatButtonModule,
    MatSelectModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './rfq-key-dates.component.html',
  styleUrl: './rfq-key-dates.component.css',
})
export class RfqKeyDatesComponent implements OnInit {
  @Output() keyDatesDataEmitter: EventEmitter<any> = new EventEmitter<any>();

  @Input() ITTDeveloperData: any;
  projectIdentifier: string;
  projectName: string;
  projectId: number;

  closeModal = true;
  issueDateControl = new FormControl('', Validators.required);
  clarificationDeadlineControl = new FormControl('', Validators.required);
  contractAwardNotificationControl = new FormControl('', Validators.required);
  contractStartDateControl = new FormControl('', Validators.required);
  projectCompletionDateControl = new FormControl('', Validators.required);

  constructor(
    private rfqWrapperComponent: RfqWrapperComponent,
    private router: Router,
    private requestForQuotationService: RequestForQuotationService,
    private toast: NgToastService,
    private route: ActivatedRoute
  ) {}
  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.projectName = params['projectName'];
    });
    this.route.paramMap.subscribe((params) => {
      this.projectId = params.get('projectId')
        ? parseInt(params.get('projectId'))
        : null;
    });

    /**
     * NOTE: On edit or when we go back and forth between wizards, we will
     *  set initial value if it exists
     */
    if (
      this.ITTDeveloperData?.issue_date &&
      this.ITTDeveloperData?.clarification_deadline &&
      this.ITTDeveloperData?.contract_award_notification &&
      this.ITTDeveloperData?.contract_start_date &&
      this.ITTDeveloperData?.project_completion_date
    ) {
      this.issueDateControl.setValue(this.ITTDeveloperData?.issue_date);
      this.clarificationDeadlineControl.setValue(
        this.ITTDeveloperData?.clarification_deadline
      );
      this.contractAwardNotificationControl.setValue(
        this.ITTDeveloperData?.contract_award_notification
      );
      this.contractStartDateControl.setValue(
        this.ITTDeveloperData?.contract_start_date
      );
      this.projectCompletionDateControl.setValue(
        this.ITTDeveloperData?.project_completion_date
      );
    }
  }

  prepareDataToSubmit() {
    const datePipe = new DatePipe('en-US');

    // Mark controls as touched to trigger validation UI
    this.issueDateControl.markAsTouched();
    this.clarificationDeadlineControl.markAsTouched();
    this.contractAwardNotificationControl.markAsTouched();
    this.contractStartDateControl.markAsTouched();
    this.projectCompletionDateControl.markAsTouched();

    // Check if all required fields have values
    if (
      this.issueDateControl.value &&
      this.clarificationDeadlineControl.value &&
      this.contractAwardNotificationControl.value &&
      this.contractStartDateControl.value &&
      this.projectCompletionDateControl.value
    ) {
      const data = {
        issue_date: datePipe.transform(
          this.issueDateControl.value,
          'MM/dd/yyyy'
        ),
        clarification_deadline: datePipe.transform(
          this.clarificationDeadlineControl.value,
          'MM/dd/yyyy'
        ),
        contract_award_notification: datePipe.transform(
          this.contractAwardNotificationControl.value,
          'MM/dd/yyyy'
        ),
        contract_start_date: datePipe.transform(
          this.contractStartDateControl.value,
          'MM/dd/yyyy'
        ),
        project_completion_date: datePipe.transform(
          this.projectCompletionDateControl.value,
          'MM/dd/yyyy'
        ),
      };

      return data;
    }
  }

  moveToNextStep() {
    const dataToSubmit = this.prepareDataToSubmit();

    if (dataToSubmit) {
      this.requestForQuotationService
        .storeDeveloperRFQKeyDatesData(dataToSubmit, this.projectId)
        .subscribe({
          /**
           * IMP: we dont need to emit here as we are navigating to
           *  different route and when we reopen, we will get data
           *  from itt-wrapper component
           */
          next: (response: any) => {
            if (response?.meta?.success) {
              this.toast.success('Key dates submitted successfully', 'SUCCESS');
              this.keyDatesDataEmitter.emit(response?.data);
              this.router.navigate(
                ['/initiate-rfq', this.projectId, response?.data?.id],
                {
                  queryParams: {
                    projectName: this.projectName,
                    projectIdentifier: this.projectIdentifier,
                  },
                }
              );
              this.rfqWrapperComponent.nextStep();
              return;
            }
            this.toast.danger('Key dates could not be saved', 'DANGER', 4000);
          },
          error: (error: any) => {
            console.error('Failed to submit:', error);
            this.toast.danger(error.error.message, 'DANGER', 4000);
          },
        });
    } else {
      this.toast.warning('Please fill the required fields', 'warning', 4000);
    }
  }

  moveToPreviousStep() {
    this.rfqWrapperComponent.previousStep();
  }
}
